import '/modules/common/css/ui/ui.css';
import '/modules/common/css/ui/ui.mobile.css';
import * as $ from "jquery";
import {Translator} from "../locale/Translator";
import {isString, nnTrim} from "../util";
import isMobile from "is-mobile";
import {StatsGatherer} from "../StatsGatherer";
import {Tooltip} from "../ui/Tooltip";
import {MAX_SMALL_HEIGHT_THRESHOLD, MAX_SMALL_WIDTH_THRESHOLD} from "../config/config";
import {Base} from "../Base";
import {ContactButton} from "../ui/contact";
import {Modal2Thirds, ModalHalf} from "../ui/Modal";
import {WarningsButton} from "../ui/warnings";
import {UIElement} from "../ui/ui";
import {formatTimestamp} from "../time/TimeCalculator";

function initNamespace() {
    if (!window.tumbo)
        window.tumbo = {}
}


/**
 * The main class of a Tumbo application
 * Should be extended to create a new Tumbo App
 * @abstract
 * @extends Base
 */
class MainTumboApplication extends Base {

    static MAX_RUNTIMECONFIG_SESSION_TIME = 2 * 60 * 60 * 1000; //after 2 hours of "sleep" the application forgets its session and reloads as default

    /**
     *
     * @param translations {object} A dictionary of translation keys to translations
     * @param config {object} The application configuration to be parsed
     * @param targetId {string} The DOM element id to render this application in
     * @param logger {Logger}
     */
    constructor(translations, config, targetId, logger) {
        super();
        tumbo.LOGGER = logger;
        this.translator = new Translator(translations);

        this.config = config;
        this.targetId = targetId;
        if (targetId) {
            if (isString(targetId)) {
                this.target = $('#' + targetId);
                if (this.target.length === 0) {
                    throw 'No element found for targetId';
                }
            } else {
                throw 'Target must be the id of a DOM element';
            }
        } else {
            throw 'No target';
        }
    }

    /**
     * log stat entry for this application
     * @param action {string}
     * @param store {boolean} If true, stores immediately
     */
    logStat(action, store = false) {
        tumbo.STATS.logByCode(this.parsedConfig.getCode(), nnTrim(this.getClassName(), this.getClass()), action, store);
    }

    /**
     * Loads and validates the application configuration
     */
    loadConfig() {
        this.parsedConfig = this._readConfig();

        if (this.verifyErrors(true)) {
            if (this._getRuntimeConfigSerializationKey()) {
                try {
                    const runtimeConfigSerialization = this._getRuntimeConfigSerialization();
                    this.runtimeConfig = this._getRuntimeConfigFromSerialization(runtimeConfigSerialization);
                    this.parsedConfig.override(this.runtimeConfig);
                    if (!this.verifyErrors()) {
                        this.parsedConfig.reset();
                        this.runtimeConfig.clean();
                    }
                    tumbo.appCode = this.parsedConfig.getCode();
                } catch (e) {
                    this.runtimeConfig = this._getRuntimeConfig();
                    this.logError("Could not read runtime config", e);
                }
            }
        } else {
            this.error = true;
        }
    }

    _getRuntimeConfigSerialization() {
        if (this._isRuntimeConfigOutdated()) {
           this._clearRuntimeConfig();
        }
        return localStorage.getItem(this._getRuntimeConfigSerializationKey());
    }

    _clearRuntimeConfig(){
        localStorage.removeItem(this._getRuntimeConfigSerializationKey() + '_ts');
        localStorage.removeItem(this._getRuntimeConfigSerializationKey());
        this.logInfo("Clear localStorage config");
    }

    _isRuntimeConfigOutdated() {
        const tsStr = nnTrim(localStorage.getItem(this._getRuntimeConfigSerializationKey() + '_ts'));
        const ts =  tsStr ? new Date(tsStr) : undefined;
        return ts && Math.abs(new Date() - ts) > MainTumboApplication.MAX_RUNTIMECONFIG_SESSION_TIME;
    }

    _setRuntimeConfigTS() {
        localStorage.setItem(this._getRuntimeConfigSerializationKey() + '_ts',formatTimestamp(new Date(), true));
    }

    _getRuntimeConfig() {
    }

    _getRuntimeConfigFromSerialization(runtimeConfigSerialization) {
    }

    _readConfig() {
    }

    _getRuntimeConfigSerializationKey() {
    }

    verifyErrors(write) {
        const errors = this.parsedConfig.verify();
        if (write && errors.length > 0 && tumbo.LOGGER.isOnDebug()) {
            const errStr = errors.join('<br/>');
            this.target.append($('<pre>' + errStr + '</pre>'));
        }
        errors.forEach(e => this.logError(e));
        return errors.length === 0;
    }

    /**
     * Runs this application. To be called from the outside.
     * Should not be overridden
     */
    run() {
        if (!this.error) {
            this.target.addClass('ccl-color_emergency ccl-style tumbo');

            this.isMobileDevice = isMobile();
            if (this.isMobileDevice) {
                this.target.addClass('mobile-device');
            } else {
                this.target.removeClass('mobile-device');
            }


            this.tooltip = new Tooltip(this.parsedConfig.getCode() + 'tooltip', this.translator, this.isMobileDevice, this.target);

            this._initSizeMonitoring();

            this._doRun();

            tumbo.STATS = new StatsGatherer(this.translator, this.parsedConfig.getCode(), this.parsedConfig.getStatsPostUrl(), this.isMobileDevice, this.target);
            tumbo.STATS.initSendProcedure();
            this.logStat("INIT");

            const contactPostUrl = this.parsedConfig.getContactPostUrl();
            if (contactPostUrl) {
                const modal = this.isSmallContainerWidth
                    ? new Modal2Thirds(this.translator, this.isMobileDevice, this.target, 'contact_form_modal', this.runtimeConfig, this.getOnChangeRuntimeConfigCallback(), true)
                    : new ModalHalf(this.translator, this.isMobileDevice, this.target, 'contact_form_modal', this.runtimeConfig, this.getOnChangeRuntimeConfigCallback(), true);
                this.contactButton = new ContactButton(this.translator, this.isMobileDevice, modal, contactPostUrl, this.runtimeConfig, this.getOnChangeRuntimeConfigCallback());
                this.target.append(this.contactButton.getUIElement());
            }

            this.isRunning = true;

            this.onSizeChange();
        }
        if (window.tumbo.tumboColors) {
            const elementById = document.getElementsByTagName('body')[0];
            for (let name in window.tumbo.tumboColors) {
                elementById.style.setProperty('--' + name, window.tumbo.tumboColors[name]);
            }
        }
    }

    _initWarnings() {
        this.warningsButton = new WarningsButton(
            this.parsedConfig.getGeneralWarningMessages(),
            this.parsedConfig.getAllServiceWarningMessages(),
            this.target,
            this.translator,
            this.isMobileDevice,
            this.runtimeConfig,
            this.getOnChangeRuntimeConfigCallback()
        );
        this.warningsButton.onChangeVisibleLayers([]);
    }

    /**
     * Should be overridden to implement this application
     * @private
     */
    _doRun() {
    }

    /**
     * Should be overridden to make this application react to changes in window size
     */
    onSizeChange() {
        UIElement.allUIElements.forEach(e => {
            e.setContainerSizeAndPosition(this.isSmallContainerWidth, this.isSmallContainerHeight, this.containerWidth, this.containerHeight, this.containerAbsolutePosition);
        });
    }

    /**
     * Should not be overridden
     * @protected
     */
    _initSizeMonitoring() {
        this.target.append($(
            '<div class="landscape-error">' +
            '<div class="landscape-error-message">' + this.translator.translate('landscapeErrorMessage') + '</div>' +
            '</div>'
        ));

        const onResize = () => {
            this.containerWidth = this.target.width();
            this.containerHeight = this.target.height();
            this.containerAbsolutePosition = this.target.offset();
            this.isSmallContainerWidth = this.containerWidth < MAX_SMALL_WIDTH_THRESHOLD
            this.isSmallContainerHeight = this.containerHeight < MAX_SMALL_HEIGHT_THRESHOLD;
            if (this.containerHeight < MAX_SMALL_HEIGHT_THRESHOLD / 2 && this.containerWidth > this.containerHeight * 1.5) {
                $('.landscape-error', this.target).show();
            } else $('.landscape-error', this.target).hide();

            if (this.isSmallContainerWidth) {
                this.target.addClass('small-tumbo-width').addClass('small-tumbo');
            } else {
                this.target.removeClass('small-tumbo-width');
            }
            if (this.isSmallContainerHeight) {
                this.target.addClass('small-tumbo-height').addClass('small-tumbo');
            } else {
                this.target.removeClass('small-tumbo-height');
            }
            if (!this.isSmallContainerHeight && !this.isSmallContainerWidth) {
                this.target.removeClass('small-tumbo');
            }

            if (this.isRunning)
                this.onSizeChange();
        }
        new ResizeObserver(onResize).observe(this.target[0]);
        onResize();
    }

    onChangeRuntimeConfig() {
        this.parsedConfig.override(this.runtimeConfig);
    }

    getOnChangeRuntimeConfigCallback() {
        return () => this.onChangeRuntimeConfig()
    }

    initRuntimeConfigUpdate() {
        const key = this._getRuntimeConfigSerializationKey();
        if (key) {
            if (this.runtimeConfigIntervalId) {
                clearInterval(this.runtimeConfigIntervalId);
            }
            this.clearOnErrorCount = 0;
            this.runtimeConfigIntervalId = setInterval(() => {
                const serializedConfig = this.runtimeConfig.getSerializedConfig();
                if(this._isRuntimeConfigOutdated()){
                    this._clearRuntimeConfig();
                    if(tumbo.LOGGER.isOnDebug())
                        this.logDebug("Would refresh page");
                    else location.reload();
                }
                if (tumbo.LOGGER.isOnDebug() && new Date().getMinutes() % 5 === 0 && new Date().getSeconds() === 0) {
                    this.logDebug("Saving runtimeConfig with " + serializedConfig.length + " chars");
                    // this.logDebug("Saving runtimeConfig " + JSON.stringify(this.runtimeConfig.getConfigOverrideObject()));
                }
                try {
                    this._setRuntimeConfigTS();
                    localStorage.setItem(key, serializedConfig);
                    this.clearOnErrorCount = 0;
                } catch (e) {
                    this.logError("Error saving runtime config to localStorage", e);
                    if (this.clearOnErrorCount < 3) {
                        localStorage.clear();
                        this.clearOnErrorCount++;
                        this.logInfo("Cleared localStorage");
                    } else {
                        clearInterval(this.runtimeConfigIntervalId);
                        this.logError('Already cleared localStorage ' + this.clearOnErrorCount + ' times. Will clear and reload.');
                        setTimeout(() => {
                            localStorage.clear();
                            location.reload();
                        }, 5000);
                    }
                }
            }, 1000);
        }
    }
}

/**
 * Set a color by its name. It will be used in the map style.
 * Possible color names:
 *     . fundamental
 *     . fundamental-half-opacity
 *     . fundamental-contrast
 *     . background-disabled
 *     . text-background-disabled
 *     . border-disabled
 *     . background-table-header
 *     . text
 *     . text-disabled
 *     . text-disabled-light
 *     . text-context
 *
 * Call to this function should be made before calling the init function
 *
 * @param colorName {string}
 * @param color {string} Any CSS color
 */
function setColor(colorName, color) {
    if (!window.tumbo.tumboColors)
        window.tumbo.tumboColors = {};
    window.tumbo.tumboColors[colorName] = color
}

initNamespace();
window.tumbo.setColor = setColor;

export {MainTumboApplication, initNamespace, setColor}